import React, { useContext, useRef, useMemo } from "react";
import { AccountContext } from "@context/AccountProvider";
import { Box, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import ClubForm from '@component/ClubForm';

export default function ClubPage() {
  const navigate = useNavigate();
  const { sites } = useContext(AccountContext);
  const containerRef = useRef(null);
  const { t } = useTranslation("main");

  // Combine address fields into a single string
  const formattedSites = useMemo(() => {
    if (!sites) return [];
    
    return sites.map(site => ({
      ...site,
      id: site.uuid,
      location: [
        site.adr_street,
        site.adr_unit,
        site.adr_sub_region,
        site.adr_state_or_region,
        site.adr_postcode_or_zip,
        site.adr_country_code
      ]
      .filter(Boolean) // Remove empty fields
      .join(', ')
    }));
  }, [sites]);

  const columns = useMemo(() => [
    { 
      field: 'name', 
      headerName: t("name"), 
      sortable: false,
      filterable: false,
      flex: 1,
    },
    { 
      field: 'location', 
      headerName: t("address"), 
      sortable: false,
      filterable: false,
      flex: 2,
    }
  ], [t]);

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        maxWidth: '1700px',
        p: 2,
        mx: 'auto'
      }}
    >
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        mb: 4,
        alignItems: 'flex-start',
        justifyContent:'center'
      }}>
        {/* ClubForm Section */}
        <Box sx={{ 
          flex: '1 1 400px', // Grow, shrink, min-width of 400px
          maxWidth: '700px',
          minWidth: '300px'
        }}>
          <Typography variant="h5" gutterBottom textAlign="left">
            {t("club")}
          </Typography>
          <ClubForm />
        </Box>

        {/* DataGrid Section */}
        {formattedSites?.length > 0 && (
          <Box sx={{ 
            flex: '2 1 600px', // More flexible than the form
            minWidth: '300px',
            overflow: 'hidden'
          }}>
            <Typography variant="h5" gutterBottom>
              {t("site_other")}
            </Typography>
            <DataGrid
              rows={formattedSites}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              onRowClick={(params) => navigate(`/admin/site/${params.id}`)}
              sx={{ 
                cursor: 'pointer',
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
              autoHeight
              disableSelectionOnClick
              disableRowSelectionOnClick
              hideFooter={true}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}